import { FC, useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  MenuItem,
  Select
} from '@mui/material';

import MDBox from '@components/MDBox';
import MDButton from '@components/MDButton';
import MDInput from '@components/MDInput';
import MDTypography from '@components/MDTypography';
import { useGetFeedbackTopicChoicesQuery } from '@api/projects';
import { sendAmplitudeData } from '@utils/amplitude';
import { PAGE_VISITED, SUBMIT_FEEDBACK } from '@constants/eventNames';
import { sendGAData } from '@utils/googleAnalytics';
import { getPageName } from '@utils/common';
import { useSelector } from 'react-redux';
import { RootState } from '@store/store';
import CloseButton from '@components/CloseButton';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmitFeedBack: (feedback: any) => void;
}

const FeedBackModal: FC<Props> = ({ open, onClose, onSubmitFeedBack }) => {
  const userDetails = useSelector((state: RootState) => state?.userDetails);

  const [feedBackData, setFeedBackData] = useState({
    feedback: '',
    topic: '',
    email: userDetails?.user.email || ''
  });

  const [errorData, setErrorData] = useState({
    feedback: '',
    topic: '',
    email: ''
  });

  const { data } = useGetFeedbackTopicChoicesQuery({});

  const validateEmail = (email: string) => {
    // Regular expression for validating email addresses
    const emailRegex = /^[\w.+-]+@[\w.-]+\.\w+$/;

    return emailRegex.test(email);
  };

  const handleClose = () => {
    setFeedBackData({ feedback: '', topic: '', email: '' });
    setErrorData({ feedback: '', topic: '', email: '' });
    onClose();
  };

  const handleValueChange = (e: any) => {
    const { value, name } = e.target;

    setFeedBackData({ ...feedBackData, [name]: value });

    if (name === 'feedback' && value.length > 1000)
      setErrorData({ ...errorData, [name]: 'Character Limit 1000 exceeded' });

    if (name === 'email' && value.length > 50)
      setErrorData({ ...errorData, [name]: 'Character Limit 50 exceeded' });

    if (
      (name === 'topic' && value && errorData.topic) ||
      (name === 'email' && value.length <= 50 && errorData.email) ||
      (name === 'feedback' && value.length <= 1000 && errorData.feedback)
    )
      setErrorData({ ...errorData, [name]: '' });
  };

  const handlePostComment = () => {
    if (errorData.email || errorData.feedback || errorData.topic) return;
    if (
      feedBackData.email.trim() === '' ||
      !validateEmail(feedBackData.email)
    ) {
      setErrorData({
        ...errorData,
        email: 'Please enter a valid E-mail Address'
      });

      return;
    }

    if (feedBackData.topic.trim() === '') {
      setErrorData({
        ...errorData,
        topic: 'Please select a Topic and then click Submit'
      });

      return;
    }
    if (feedBackData.feedback.trim() === '') {
      setErrorData({
        ...errorData,
        feedback: 'Please provide a description'
      });

      return;
    }

    onSubmitFeedBack({
      feedback_text: feedBackData.feedback,
      url_in_address_bar: window.location.href,
      classification: feedBackData.topic,
      email: feedBackData.email
    });

    sendAmplitudeData(SUBMIT_FEEDBACK, { 'Support Type': feedBackData.topic });
    sendGAData(SUBMIT_FEEDBACK, { 'Support Type': feedBackData.topic });
    handleClose();
  };

  useEffect(() => {
    sendAmplitudeData(PAGE_VISITED, {
      'Page Name': 'Contact Support Modal',
      Navigation: getPageName()
    });
    sendGAData(PAGE_VISITED, {
      'Page Name': 'Contact Support Modal',
      Navigation: getPageName()
    });
  }, []);

  return (
    <Dialog
      open={open}
      fullWidth
      onClose={handleClose}
      PaperProps={{ sx: { p: 1 } }}
      sx={{
        '& .MuiPaper-root': {
          position: 'relative',
          overflow: 'visible'
        }
      }}
    >
      <CloseButton onClose={() => handleClose()} />
      <DialogTitle {...({ component: 'div' } as any)}>
        <MDTypography variant='h3' mb={1} color='secondary' textGradient>
          Contact Support
        </MDTypography>
        <MDTypography variant='h5' color='info' sx={{ fontWeight: 400, mb: 1 }}>
          Please select a topic and provide a description below
        </MDTypography>
      </DialogTitle>
      <DialogContent>
        <MDBox display={'flex'} flexDirection={'column'} rowGap={1} my={2}>
          <MDTypography variant='h6' color='dark'>
            E-mail Address
          </MDTypography>
          <MDInput
            color='info'
            name='email'
            value={feedBackData.email}
            disabled={userDetails.user.email}
            error={errorData.email.length > 0 ? true : false}
            onChange={handleValueChange}
          />
          {errorData.email.length > 0 && (
            <FormHelperText error>{errorData.email}</FormHelperText>
          )}
          <MDTypography variant='h6' color='dark' sx={{ mt: 3 }}>
            Select a Topic
          </MDTypography>
          <Select
            color='info'
            name='topic'
            value={feedBackData.topic}
            error={errorData.topic.length > 0 ? true : false}
            sx={{ padding: '10px' }}
            onChange={handleValueChange}
            displayEmpty
            renderValue={
              feedBackData.topic !== '' ? undefined : () => <>Select a Topic</>
            }
          >
            {data?.classification_choices.map((choice: any) => (
              <MenuItem value={choice[0]} key={choice[0]}>
                {choice[1]}
              </MenuItem>
            ))}
          </Select>
          {errorData.topic.length > 0 && (
            <FormHelperText error>{errorData.topic}</FormHelperText>
          )}
          <MDTypography variant='h6' color='dark' mt={3}>
            Full Description
          </MDTypography>
          <MDInput
            color='info'
            name='feedback'
            value={feedBackData.feedback}
            onChange={handleValueChange}
            multiline
            rows={5}
            error={errorData.feedback.length > 0 ? true : false}
          />
        </MDBox>
        {errorData.feedback.length > 0 && (
          <FormHelperText error>{errorData.feedback}</FormHelperText>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column'
        }}
      >
        <MDButton
          color='primary'
          variant='gradient'
          onClick={handlePostComment}
        >
          <MDTypography variant='xs' color='white'>
            SUBMIT
          </MDTypography>
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

export default FeedBackModal;
